// The right of withdrawal (angrerett) as it must be put in front of someone
// before they buy (angrerettloven § 8 h and j), in the wording of the state's
// model forms Q-0312B and Q-0319B.
//
// Shown in two places: the Subscription section in Settings (subscription.js)
// and the plan chooser (plan-choice.js). The confirmation email
// (functions/lib/billing-emails.js) says the same after the purchase — change
// the three together. Both languages sit here rather than in no.js for the
// reason privacy-policy.js gives: a legal text cut into paragraph-long
// dictionary keys drifts apart. `{…}` is filled from SELLER (seller.js).

import { currentLang } from '../services/functions-api.js';
import { SELLER, sellerAddress, sellerLine } from './seller.js';

const WITHDRAWAL_INFO = {
    no: {
        paragraphs: [
            'Du har rett til å gå fra avtalen innen 14 dager uten å oppgi noen grunn for dette. Angrefristen utløper 14 dager etter inngåelsen av avtalen.',
            'For å kunne bruke angreretten må du underrette oss på en utvetydig måte om din beslutning om å gå fra avtalen: med knappen «Angre kjøpet» under Innstillinger → Abonnement, i en e-post til {email}, eller i et brev til {name}, {address}. Du kan benytte angreskjemaet nedenfor, men det er ikke obligatorisk. Bruker du knappen, sender vi deg umiddelbart et mottaksbevis på e-post.',
            'For å overholde angrefristen er det tilstrekkelig at du sender meldingen om at du vil bruke angreretten før angrefristen utløper.',
            'Dersom du går fra avtalen, skal vi tilbakebetale alle betalinger vi har mottatt fra deg, uten unødig opphold og i alle tilfeller senest 14 dager etter den dag vi mottar melding om din beslutning. Vi foretar tilbakebetalingen med samme betalingsmiddel som du benyttet, med mindre du uttrykkelig har avtalt noe annet med oss, og du blir ikke pålagt noe gebyr.',
            'Den første gangen du abonnerer, starter abonnementet med en gratis prøveperiode. Går du fra avtalen mens den varer, betaler du ingenting.',
            'Dersom du har anmodet om at tjenesten skal påbegynnes før angrefristen utløper, skal du betale oss et beløp som står i forhold til det som er levert fram til du underrettet oss om at du ønsket å gå fra avtalen, sammenlignet med full oppfyllelse av avtalen.',
            'Selger: {seller}.'
        ],
        formHeading: 'Angreskjema (ved kjøp av varer og tjenester som ikke er finansielle tjenester)',
        form: [
            'Fyll ut og returner dette skjemaet bare dersom du ønsker å gå fra avtalen.',
            'Utfylt skjema sendes til: {seller}',
            'Jeg/vi underretter herved om at jeg/vi ønsker å gå fra min/vår avtale om kjøp av følgende tjenester: Zugzwang turneringslederkonto',
            'Avtalen ble inngått den (dato):',
            'Forbrukerens/forbrukernes navn:',
            'Forbrukerens/forbrukernes adresse:',
            'Dato:',
            'Forbrukerens/forbrukernes underskrift (bare dersom papirskjema benyttes):'
        ]
    },
    en: {
        paragraphs: [
            'You have the right to withdraw from this agreement within 14 days without giving any reason. The withdrawal period expires 14 days after the day the agreement was made.',
            'To exercise the right of withdrawal, you must inform us of your decision by an unequivocal statement: with the "Withdraw from purchase" button under Settings → Subscription, by email to {email}, or by letter to {name}, {address}. You may use the withdrawal form below, but it is not obligatory. If you use the button, we will send you an acknowledgement of receipt by email straight away.',
            'To meet the withdrawal deadline, it is sufficient to send your communication before the withdrawal period has expired.',
            'If you withdraw, we shall reimburse all payments received from you without undue delay, and in any event not later than 14 days from the day we are informed of your decision. We use the same means of payment you used, unless you have expressly agreed otherwise, and you will not incur any fees.',
            'The first time you subscribe, the subscription starts with a free trial. If you withdraw while it lasts, you pay nothing.',
            'If you requested that the service begin during the withdrawal period, you shall pay us an amount proportionate to what has been provided until you informed us of your withdrawal, in comparison with the full coverage of the agreement.',
            'Seller: {seller}.'
        ],
        formHeading: 'Model withdrawal form (goods and services that are not financial services)',
        form: [
            'Complete and return this form only if you wish to withdraw from the agreement.',
            'To: {seller}',
            'I/We hereby give notice that I/we withdraw from my/our agreement for the provision of the following service: Zugzwang arbiter account',
            'Agreement made on (date):',
            'Name of consumer(s):',
            'Address of consumer(s):',
            'Date:',
            'Signature of consumer(s) (only if this form is notified on paper):'
        ]
    }
};

function fillSeller(text) {
    const values = { name: SELLER.name, address: sellerAddress(), email: SELLER.email, seller: sellerLine() };
    return text.replace(/\{(name|address|email|seller)\}/g, (m, key) => values[key]);
}

/** Draw the angrerett text, and the form, into `body`. */
export function renderWithdrawalInfo(body) {
    if (!body) return;
    const info = WITHDRAWAL_INFO[currentLang() === 'en' ? 'en' : 'no'];
    const p = (text, css) => {
        const el = document.createElement('p');
        el.style.cssText = `margin: 0 0 0.6rem; ${css || ''}`;
        el.textContent = fillSeller(text);
        return el;
    };
    body.replaceChildren(
        ...info.paragraphs.map(t => p(t)),
        p(info.formHeading, 'margin-top: 1rem; font-weight: 600; color: var(--text-main);'),
        ...info.form.map(t => p(t, 'margin-bottom: 0.35rem;'))
    );
}
